import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Badge } from '../../ui-v2';
import { cn } from '../../../../lib/cn';
import { buscarPersonas } from '../api';

/**
 * PersonaPicker — autocomplete de selección ÚNICA para "crear ticket en nombre de"
 * (Soporte 3.5.G). Busca en GET /api/soporte/personas/buscar (internos + colaboradores).
 *
 * - Debounce 300ms; mínimo 2 caracteres antes de disparar la búsqueda.
 * - Dropdown bajo el input (no modal), mismo ancho, máx. ~300px con scroll.
 * - Cierra al click fuera y con Escape.
 * - Al seleccionar → chip cerrable con nombre + tipo + email (+ empresa si colaborador).
 * - No busca al montar; con `value` inicial no-null muestra el chip directamente.
 *
 * props:
 *   value    { id, nombre, email, tipo:'interno'|'colaborador', empresa } | null
 *   onChange (persona|null) => void
 *   disabled bool
 *   error    string  (error inline del backend: persona_no_encontrada, etc.)
 */
const MIN_CHARS = 2;

export default function PersonaPicker({ value, onChange, disabled = false, error }) {
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [buscado, setBuscado] = useState(false); // hubo al menos una búsqueda con q válido
  const wrapRef = useRef(null);

  // Búsqueda con debounce 300ms; solo con q >= 2 chars y sin persona ya elegida.
  useEffect(() => {
    if (value) return;
    const t = q.trim();
    if (t.length < MIN_CHARS) {
      setResultados([]); setCargando(false); setBuscado(false);
      return undefined;
    }
    setCargando(true);
    const handle = setTimeout(async () => {
      try {
        const data = await buscarPersonas(t);
        setResultados(Array.isArray(data?.personas) ? data.personas : []);
      } catch {
        setResultados([]);
      } finally {
        setCargando(false);
        setBuscado(true);
        setAbierto(true);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [q, value]);

  // Cerrar el dropdown al hacer click fuera.
  useEffect(() => {
    if (!abierto) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [abierto]);

  const seleccionar = (p) => {
    onChange?.(p);
    setQ(''); setResultados([]); setAbierto(false); setBuscado(false);
  };
  const limpiar = () => {
    onChange?.(null);
    setQ(''); setResultados([]); setBuscado(false);
  };

  // ── Estado seleccionado → chip ──────────────────────────────────────────
  if (value) {
    return (
      <div>
        <div className="flex items-center justify-between gap-2 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-800 truncate">{value.nombre}</span>
              <TipoBadge tipo={value.tipo} />
            </div>
            <div className="text-xs text-slate-500 truncate">
              {[value.email, value.tipo === 'colaborador' ? value.empresa : null].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          {!disabled && (
            <button type="button" onClick={limpiar} className="text-slate-400 hover:text-slate-700 shrink-0" aria-label="Quitar persona">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  // ── Estado de búsqueda ──────────────────────────────────────────────────
  const q2 = q.trim();
  const hint1char = q2.length === 1;
  const mostrarDropdown = abierto && q2.length >= MIN_CHARS;

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={q}
          disabled={disabled}
          onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
          onFocus={() => { if (q2.length >= MIN_CHARS) setAbierto(true); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setAbierto(false); }}
          placeholder="Nombre, apellidos o email…"
          className={cn(
            'w-full border rounded-md pl-8 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30',
            error ? 'border-red-300' : 'border-slate-200',
            disabled && 'opacity-60 cursor-not-allowed',
          )}
        />
      </div>

      {hint1char && <p className="mt-1 text-xs text-slate-400">Escribe al menos 2 caracteres.</p>}
      {error && !hint1char && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {mostrarDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-[300px] overflow-y-auto">
          {cargando ? (
            <div className="px-3 py-4 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
            </div>
          ) : resultados.length === 0 ? (
            buscado ? <div className="px-3 py-4 text-sm text-slate-400">No se han encontrado personas con ese texto.</div> : null
          ) : (
            <ul className="divide-y divide-slate-100">
              {resultados.map((p) => (
                <li key={`${p.tipo}:${p.id}`}>
                  <button
                    type="button"
                    onClick={() => seleccionar(p)}
                    className="w-full px-3 py-2 flex items-start justify-between gap-3 hover:bg-slate-50 text-left"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{p.nombre}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {[p.email, p.tipo === 'colaborador' ? p.empresa : null].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                    <TipoBadge tipo={p.tipo} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Badge de tipo — mismos colores que OrigenBadge del panel (colaborador=warning, interno=secondary).
function TipoBadge({ tipo }) {
  return tipo === 'colaborador'
    ? <Badge variant="warning" className="text-[10px] shrink-0">Colaborador</Badge>
    : <Badge variant="secondary" className="text-[10px] shrink-0">Interno</Badge>;
}
